import { default as axios, AxiosResponse, AxiosRequestConfig } from 'axios'
import { Logger } from '../../shared/Logger'
import { readDeviceCode } from './authStorage'
import { AuthClient } from './IdentityTypes'

export type AppRequestOptions =
  | {
      noAuthorization: true
    }
  | {
      noAuthorization?: boolean
      sessionToken: string
      userId: string
      deviceCode?: string
    }

export const noauthOption: AppRequestOptions = { noAuthorization: true }

export const post = async <T>(
  domain: string,
  path: string,
  body: object,
  options: AppRequestOptions,
): Promise<T> => {
  Logger.debug(`posting ${domain}/${path} with ${JSON.stringify(body)}`)
  const config: AxiosRequestConfig = buildConfig(options)
  const response = await axios.post<T, AxiosResponse<T>>(
    `${domain}/${path}`,
    body,
    config,
  )
  Logger.debug(`post response was ${JSON.stringify(response)}`)
  return response.data
}

export const get = async <T>(
  domain: string,
  path: string,
  options: AppRequestOptions,
): Promise<T> => {
  Logger.debug(`getting  ${domain}/${path}`)
  const config: AxiosRequestConfig = buildConfig(options)
  const response = (await axios.get(
    `${domain}/${path}`,
    config,
  )) as AxiosResponse<T>
  return response.data
}

export const getImage = async (
  url: string,
  options: AppRequestOptions,
): Promise<Blob> => {
  Logger.debug(`getting ${url}`)
  const config: AxiosRequestConfig = {
    ...buildConfig(options),
    responseType: 'blob',
  }
  const response = (await axios.get(`${url}`, config)) as AxiosResponse<Blob>
  return response.data
}

export const deleteItem = async (
  domain: string,
  path: string,
  options: AppRequestOptions,
): Promise<void> => {
  Logger.debug(`deleting  ${domain}/${path}`)
  const config: AxiosRequestConfig = buildConfig(options)
  ;(await axios.delete(`${domain}/${path}`, config)) as AxiosResponse<unknown>
}

function buildConfig(options: AppRequestOptions): AxiosRequestConfig {
  if (options.noAuthorization) {
    return getEmptyConfig()
  }
  if (!options.sessionToken) {
    throw new Error('sessionToken not provided in buildConfig')
  }
  if (!options.userId) {
    throw new Error('userId not provided in buildConfig')
  }
  const apparentDeviceCode : string | null = options.deviceCode || readDeviceCode()
  if (!apparentDeviceCode){
    throw new Error('no apparent deviceCode available in buildConfig')
  }
  return getAuthorizedConfig(getEmptyConfig(), options.userId, apparentDeviceCode, options.sessionToken)
}

function getEmptyConfig(): AxiosRequestConfig {
  return { headers: {} }
}

function getAuthorizedConfig(
  config: AxiosRequestConfig,
  userId: String,
  deviceCode: String,
  sessionToken: String
): AxiosRequestConfig {
  const authorization: String = `${userId}#${deviceCode}#${sessionToken}`
  return {
    ...config,
    headers: { ...(config.headers || {}), Authorization: `Bearer ${authorization}` },
  }
}

export const buildAuth = (authClient: AuthClient, deviceCode: string): AppRequestOptions => {
  return {
    userId: authClient.user.userId || '',
    deviceCode,
    sessionToken: authClient.sessionToken
  }
}